// assets/sticky-atc-button.js
// Sticky ATC bar + bottom-sheet size picker.
//
// Self-healing across Section Rendering API re-renders:
//   - Never caches DOM nodes that live inside <product-info>.
//   - Re-acquires #ProductSubmitButton-{section} and #product-form-{section}
//     by ID on every variant-change pub-sub event.
//   - On size pick we write the variant id silently to the hidden input.
//     We do NOT dispatch change, so we don't trigger the theme's variant
//     re-render cascade. The form picks up the new id on submit.

(function () {
  const bar = document.querySelector('[data-sticky-atc]');
  if (!bar) return;

  const sectionId = bar.dataset.sectionId;
  const needsSize = bar.dataset.needsSize === 'true';

  // Stable refs (rendered OUTSIDE <product-info>, survive re-render)
  const stickyBtn = bar.querySelector('[data-sticky-atc-btn]');
  const sheet = document.querySelector('[data-sticky-sheet]');
  const sheetSizes = sheet ? Array.from(sheet.querySelectorAll('[data-sheet-size]')) : [];
  const sheetCta = sheet ? sheet.querySelector('[data-sheet-cta]') : null;
  const sheetCloseEls = sheet ? sheet.querySelectorAll('[data-sheet-close]') : [];

  // Variants JSON for resolving size -> variant on the fly
  const variantsScript = document.querySelector('[data-sticky-variants]');
  let variants = [];
  try {
    if (variantsScript) variants = JSON.parse(variantsScript.textContent);
  } catch (e) { variants = []; }
  const sizeOptionIndex = parseInt(bar.dataset.sizeOptionIndex || '-1', 10);

  let pickedVariantId = null;
  let observer = null;

  // ---------- Live lookups ----------
  function getRealBtn() {
    return document.getElementById('ProductSubmitButton-' + sectionId);
  }
  function getProductForm() {
    return document.getElementById('product-form-' + sectionId);
  }
  function getVariantInputs() {
    const form = getProductForm();
    if (!form) return [];
    // Inputs INSIDE the form (the theme has one with class .product-variant-id
    // that the cart code reads via form.querySelector('[name=id]'))
    const inside = Array.from(form.querySelectorAll('input[name="id"]'));
    // Inputs OUTSIDE the form, associated via the `form="..."` attribute
    const outside = Array.from(document.querySelectorAll('input[name="id"][form="' + form.id + '"]'));
    const all = inside.concat(outside);
    return all.filter(function (el, idx) { return all.indexOf(el) === idx; });
  }
  function getCurrentVariantId() {
    const inputs = getVariantInputs();
    return inputs.length ? inputs[0].value : null;
  }
  function getCurrentVariant() {
    const id = getCurrentVariantId();
    if (!id) return null;
    return variants.find(function (v) { return String(v.id) === String(id); }) || null;
  }

  // ---------- Visibility ----------
  function recomputeVisibility() {
    const btn = getRealBtn();
    if (!btn) { bar.classList.remove('is-hidden'); return; }
    const r = btn.getBoundingClientRect();
    const visible = r.top < window.innerHeight && r.bottom > 0;
    bar.classList.toggle('is-hidden', visible);
  }

  function attachObserver() {
    if (observer) observer.disconnect();
    const btn = getRealBtn();
    if (!btn) return;
    observer = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-hidden', entries[0].isIntersecting);
    }, { threshold: 0 });
    observer.observe(btn);
  }

  // ---------- Sheet rebuild after variant change ----------
  function rebuildSheet() {
    if (!sheetSizes.length || sizeOptionIndex < 0) return;
    const current = getCurrentVariant();
    if (!current) return;

    sheetSizes.forEach(function (btn) {
      const sizeLabel = btn.dataset.sizeLabel;
      const match = variants.find(function (v) {
        if (!v.options || v.options[sizeOptionIndex] !== sizeLabel) return false;
        for (let i = 0; i < v.options.length; i++) {
          if (i === sizeOptionIndex) continue;
          if (v.options[i] !== current.options[i]) return false;
        }
        return true;
      });
      btn.dataset.variantId = match ? String(match.id) : '0';
      btn.classList.toggle('is-soldout', !(match && match.available));
      if (pickedVariantId && match && String(match.id) === String(pickedVariantId)) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  function rebind() {
    attachObserver();
    recomputeVisibility();
    rebuildSheet();
  }

  // ---------- Sheet open/close ----------
  function openSheet() {
    if (!sheet) return;
    sheet.hidden = false;
    document.body.classList.add('sticky-atc-sheet-open');
    void sheet.offsetWidth;
    sheet.classList.add('is-open');
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.classList.remove('sticky-atc-sheet-open');
    setTimeout(function () { sheet.hidden = true; }, 300);
  }

  // ---------- Pick a size (silent write — no change dispatch) ----------
  function pickSize(btn) {
    if (btn.classList.contains('is-soldout')) return;
    const variantId = btn.dataset.variantId;
    if (!variantId || variantId === '0') return;

    sheetSizes.forEach(function (b) { b.classList.remove('is-active'); });
    btn.classList.add('is-active');
    pickedVariantId = variantId;

    // Libera el candado de "talla obligatoria" de product-variant-picker.liquid
    const form = getProductForm();
    if (form) form.setAttribute('data-size-picked', '');

    getVariantInputs().forEach(function (input) {
      input.value = variantId;
      input.removeAttribute('disabled');
      input.disabled = false;
    });

    if (sheetCta) sheetCta.disabled = false;
  }

  // ---------- Submit ----------
  function assertPickedVariantOnInputs() {
    if (!pickedVariantId) return;
    getVariantInputs().forEach(function (input) {
      input.value = pickedVariantId;
      input.removeAttribute('disabled');
      input.disabled = false;
    });
  }

  function submitToCart(loaderEl) {
    if (loaderEl) loaderEl.classList.add('is-loading');

    setTimeout(function () {
      // Re-assert the picked variant id right before submit: defends against
      // any section re-render that could have reset the inputs to the
      // default current_variant.id between pickSize and submit.
      assertPickedVariantOnInputs();

      const realBtn = getRealBtn();
      const form = getProductForm();

      try {
        if (realBtn) {
          realBtn.removeAttribute('disabled');
          realBtn.disabled = false;
          realBtn.click();
        } else if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else if (form) {
          form.submit();
        }
      } catch (err) {
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
        else if (form) form.submit();
      }

      setTimeout(function () {
        if (loaderEl) loaderEl.classList.remove('is-loading');
        closeSheet();
      }, 700);
    }, 50);
  }

  // ---------- Wire events ----------
  if (stickyBtn) {
    stickyBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (needsSize) openSheet();
      else submitToCart(stickyBtn);
    });
  }

  if (sheet) {
    sheetCloseEls.forEach(function (el) { el.addEventListener('click', closeSheet); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) closeSheet();
    });
    sheetSizes.forEach(function (btn) {
      btn.addEventListener('click', function () { pickSize(btn); });
    });
    if (sheetCta) {
      sheetCta.addEventListener('click', function () {
        if (sheetCta.disabled || !pickedVariantId) return;
        submitToCart(sheetCta);
      });
    }
  }

  // ---------- Self-healing on variant change ----------
  function subscribeVariantChange() {
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (payload) {
        if (payload && payload.data && String(payload.data.sectionId) !== String(sectionId)) return;
        requestAnimationFrame(rebind);
      });
    }
    document.addEventListener('theme:product-info:loaded', function () {
      requestAnimationFrame(rebind);
    });
  }

  // Initial setup — defer one frame so theme.PUB_SUB_EVENTS is available
  requestAnimationFrame(function () {
    attachObserver();
    rebuildSheet();
    subscribeVariantChange();
  });
})();
